import React, { useState, useEffect, useMemo } from "react";
import Tabs from "../../../Shared/tabs/tabs";
import Table from "../../../Shared/table/Table";
import Chip from "../../../Shared/chip/chip";
import { callApi } from "../../../api/fetch";
import { logout } from "../../../utils/logout";
import { monthName } from "../../../utils/getMonth";
import Loading from "../../../Shared/loading/loading";
import { ACCENTCOLOR } from "../../../Shared/colors";
import { ReactComponent as CopySvg } from "../../../assets/images/copy.svg";
import { ChevronDown, ChevronUp } from "lucide-react";
import { testIdProps } from "../../../utils/testIdProps";
import {
  PaymentStatus,
  PlanStatus,
  paymentStatusMessage,
  paymentTypes,
  normalizePaymentStatus,
  normalizePlanStatus,
} from "../../../constants/orderTypes";

type HistorySectionProps = {
  planInfo?: any;
};

interface PaymentMode {
	NEFT:            string
	RTGS:            string
	IMPS:            string
	NETBANKING:      string
	UPI_PPI:         string
	DEBIT_CARD:      string
	UPI:             string
	CREDIT_CARD:     string
	UPI_CREDIT_CARD: string
	ADVANCE_PAYMENT: string
	INTERNAL_WALLET: string
	BANK_TRANSFER:   string
	OTHERS:          string
	CARD:            string
	WALLET:          string
	NA:              string
}
const PaymentModeMap: Record<keyof PaymentMode, string> = {
  NEFT: "Neft",
  RTGS: "Rtgs",
  IMPS: "Imps",
  NETBANKING: "Netbanking",
  UPI_PPI: "Upi PPI",
  DEBIT_CARD: "Debit Card",
  UPI: "UPI",
  CREDIT_CARD: "Credit Card",
  UPI_CREDIT_CARD: "Upi Credit Card",
  ADVANCE_PAYMENT: "Advance Payment",
  INTERNAL_WALLET: "Internal Wallet",
  BANK_TRANSFER: "Bank Transfer",
  OTHERS: "Other",
  CARD: "Card",
  WALLET: "Wallet",
  NA: "N/A",
} as const;

function paymentModeKey(value: unknown): keyof PaymentMode | "" {
  if (value == null || value === "") return "";
  const key = String(value)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  if (!key || key === "NA" || key === "N_A") return "NA";
  return key as keyof PaymentMode;
}

function formatPaymentMode(value: unknown): string {
  const key = paymentModeKey(value);
  if (!key) return "N/A";
  return PaymentModeMap[key] || key.replace(/_/g, " ");
}

function formatPaymentType(value: unknown): string {
  const key = paymentModeKey(value);
  if (!key) return "N/A";
  const match = paymentTypes.find((item) => item.key === key);
  if (match) return match.value;
  return key.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}



const HistorySection: React.FC<HistorySectionProps> = ({ planInfo }) => {
  const [activeTab, setActiveTab] = useState("new-billing-payment");
  const [paymentData, setPaymentData] = useState<any[]>([]);
  const [planData, setPlanData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const orgId = localStorage.getItem("organisation_id");
  if (!orgId) {
    localStorage.clear();
    logout({ redirectToLogin: true });
  }

  const fetchPaymentData = async () => {
    const response = await callApi(`/organisations/${orgId}/payments`, {
      method: "GET",
    });

    const paymentArray = Array.isArray(response)
      ? response
      : response?.data || [];
    setPaymentData(paymentArray);
  };

  const fetchPlanData = async () => {
    const response = await callApi(`/organisations/${orgId}/plans`, {
      method: "GET",
    });

    const planArray = response?.data || response || [];
    const sortedPlanArray = planArray.sort((a: any, b: any) => {
      return (b.created_at || 0) - (a.created_at || 0);
    });
    setPlanData(sortedPlanArray);
  };

  useEffect(() => {
    const loadData = async () => {
      if (orgId) {
        setLoading(true);
        setError(null);
        try {
          await Promise.all([fetchPaymentData(), fetchPlanData()]);
        } catch (error: any) {
          setError("Failed to load history data");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadData();
  }, [orgId]);

  // State for managing dropdown for addons
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);

  // Tab configuration
  const tabs = [
    { id: "new-billing-payment", label: "Payment History" },
    { id: "new-billing-plan", label: "Plan History" },
    { id: "new-billing-plan-details", label: "Plan Details" },
  ];

  const capitalizeFirstLetter = (val: string) => {
    return (
      String(val)?.charAt(0)?.toUpperCase() +
      String(val)?.slice(1)?.toLowerCase()
    );
  };

  const renderStatus = (status: string, type: "payment" | "plan") => {
    if (type === "payment") {
      const paymentStatus = normalizePaymentStatus(status);
      // Payment status logic
      if (
        paymentStatus === PaymentStatus.PAID ||
        paymentStatus === PaymentStatus.SUCCESS ||
        paymentStatus === PaymentStatus.COMPLETED
      ) {
        return (
          <Chip
            message={
              paymentStatusMessage[
                paymentStatus as keyof typeof paymentStatusMessage
              ]
            }
          ></Chip>
        );
      }
      if (paymentStatus === PaymentStatus.ACTIVE) {
        return (
          <Chip
            message={
              paymentStatusMessage[
                paymentStatus as keyof typeof paymentStatusMessage
              ]
            }
            type="processing"
          ></Chip>
        );
      }
      if (
        paymentStatus === PaymentStatus.EXPIRED ||
        paymentStatus === PaymentStatus.CANCELED ||
        paymentStatus === PaymentStatus.FAILED
      ) {
        return (
          <Chip
            message={
              paymentStatusMessage[
                paymentStatus as keyof typeof paymentStatusMessage
              ]
            }
            type="error"
          ></Chip>
        );
      }
      return (
        <Chip message={capitalizeFirstLetter(status)} type="processing"></Chip>
      );
    }

    if (type === "plan") {
      const planStatus = normalizePlanStatus(status);
      // Plan status logic
      if (planStatus === PlanStatus.ACTIVE) {
        return <Chip message="Active" type="success"></Chip>;
      }
      if (planStatus === PlanStatus.EXPIRED) {
        return <Chip message="Expired" type="error"></Chip>;
      }
      if (planStatus === PlanStatus.PAYMENT_PENDING) {
        return <Chip message="PAYMENT_PENDING" type="processing"></Chip>;
      }
      return (
        <Chip message={capitalizeFirstLetter(status)} type="processing"></Chip>
      );
    }

    // Fallback
    return (
      <Chip message={capitalizeFirstLetter(status)} type="processing"></Chip>
    );
  };

  const formatDate = (dateString: string | number) => {
    if (!dateString) return "N/A";

    let date;

    if (typeof dateString === "string") {
      // Check if it's an ISO string or timestamp string
      if (dateString.includes("T") || dateString.includes("-")) {
        // ISO string format like "2026-03-04T08:03:17.506Z"
        date = new Date(dateString);
      } else {
        // Timestamp string like "1773205798200"
        date = new Date(Number(dateString));
      }
    } else {
      // Number timestamp
      date = new Date(Number(dateString));
    }

    if (isNaN(date.getTime())) return "N/A";

    return `${monthName(date.getMonth())} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const formatAmount = (amount: number) => {
    if (!amount) return "₹ 0";
    return `₹ ${amount.toLocaleString()}`;
  };



  const copyPaymentId = async (paymentId: string) => {
    try {
      if (paymentId) {
        await navigator.clipboard.writeText(paymentId);
        setCopiedId(paymentId);
        setTimeout(() => setCopiedId(null), 2000); // Clear after 2 seconds
      }
    } catch (error) {
      console.error("Failed to copy payment ID:", error);
    }
  };

  const truncateText = (text: string) => {
    if (!text) return "N/A";

    // Get responsive max length based on screen size
    const getMaxLength = () => {
      if (window.innerWidth >= 1200) return 16; // Large screens (xl)
      if (window.innerWidth >= 992) return 12; // Desktop (lg)
      if (window.innerWidth >= 768) return 10; // Tablet (md)
      if (window.innerWidth >= 576) return 8; // Small tablet (sm)
      return 6; // Mobile (xs)
    };

    const maxLength = getMaxLength();
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  // Payment History Table Columns
  const paymentColumns = [
    {
      key: "payment_id",
      header: "Payment ID",
      render: (value: any) => (
        <div className="d-flex align-items-center gap-2 justify-content-between">
          <span title={value}>{truncateText(value)}</span>
          {copiedId != value ? (
            <CopySvg
              style={{ cursor: "pointer", width: "14px", height: "14px" }}
              onClick={() => copyPaymentId(value)}
            />
          ) : (
            <></>
          )}
          {copiedId === value && (
            <span style={{ fontSize: "10px", color: "green" }}>Copied!</span>
          )}
        </div>
      ),
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "payment_created_at",
      header: "Date",
      render: (value: any) => formatDate(value * 1000),
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "status",
      header: "Status",
      render: (value: any) => renderStatus(value || "UNKNOWN", "payment"),
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "mode",
      header: "Mode",
      render: (value: any) => formatPaymentMode(value),
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "payment_type",
      header: "Payment Type",
      render: (value: any) => formatPaymentType(value),
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "paid_amount",
      header: "Amount",
      render: (value: any) => formatAmount(value),
      className: "text-start",
      headerClassName: "text-start",
    },
  ];

  // Plan History Table Columns
  const planColumns = [
    {
      key: "assigned_at",
      header: "Date",
      render: (value: any) => formatDate(value * 1000),
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "plan_subscriptions_status",
      header: "Status",
      render: (value: any) =>
        renderStatus(value?.toUpperCase() || "UNKNOWN", "plan"),
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "plan",
      header: "Plan Name",
      render: (value: any) => (value?.plan_name) || "N/A",
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "plan",
      header: "Plan Type",
      render: (value: any) => (value?.plan_type) || "N/A",
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "plan",
      header: "Amount",
      render: (value: any) => formatAmount(value?.prepaid_recharge_amount),
      className: "text-start",
      headerClassName: "text-start",
    },
  ];

  // Product Pricing Table Columns (for Plan Details tab)
  const productPricingColumns = [
    {
      key: "product_name",
      header: "Product Name",
      render: (value: any) => value || "N/A",
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "unit_price",
      header: "Pricing",
      render: (value: any) => {
        return value === 0 ? "--" : `₹ ${value}`;
      },
      className: "text-start",
      headerClassName: "text-start",
    },
    {
      key: "unit_price",
      header: "Add On",
      render: (value: any, row: any) => {
        const hasAddons = row?.addons && row?.addons?.length > 0;
        const addOnCount = row?.addons?.length || 0;
        const isExpanded = expandedProduct === row.product_id;

        return (
          <div>
            <div className="d-flex align-items-center gap-2">
              <span>{value === 0 ? "--" : `(${addOnCount})`}</span>
              {hasAddons && (
                <div
                  {...testIdProps(`billing-history-addons-${row.product_id}`)}
                  className="d-flex align-items-center gap-1"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setExpandedProduct(isExpanded ? null : row.product_id);
                  }}
                >
                  <span style={{ fontSize: "12px", color: "#6c757d" }}>
                    View
                  </span>
                  {isExpanded ? (
                    <ChevronUp size={14} color="#6c757d" />
                  ) : (
                    <ChevronDown size={14} color="#6c757d" />
                  )}
                </div>
              )}
            </div>
            {/* Expanded addon details inline */}
            {isExpanded && hasAddons && (
              <div
                className="mt-2 p-2 bg-light rounded"
                style={{ fontSize: "12px" }}
              >
                <div className="fw-bold mb-1">{`Addon Details: (${addOnCount})`}</div>
                {row?.addons?.map((addon: any, index: number) => (
                  <div
                    key={`${addon.addon_id}-${index}`}
                    className="d-flex justify-content-between"
                  >
                    <span>{addon.addon_name}</span>
                    <span>₹ {addon.unit_price}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      },
      className: "text-start",
      headerClassName: "text-start",
    },
  ];

  // Plan Details uses plan-info (product_pricing is omitted from /plans for customers)
    // // Get active plan from organisation plan history
    // const activePlan = useMemo(() => {
    //   return (
    //     planData.find(
    //       (plan: any) =>
    //         plan.plan_subscriptions_status?.toLowerCase() === "active"
    //     ) || null
    //   );
    // }, [planData]);
  const productPricingData = useMemo(() => {
    const pricing =
      planInfo?.current?.plan?.product_pricing ??
      planInfo?.data?.current?.plan?.product_pricing;
    if (!Array.isArray(pricing)) return [];

    return pricing
      .filter((product: any) => product && (product.product_id || product.product_name))
      .map((product: any) => ({
        ...product,
        product_name:
          product.product_name?.trim() ||
          product.product_id ||
          product.product_arn ||
          "N/A",
      }));
  }, [planInfo]);

  return (
    <div
      className="p-4 d-flex flex-column"
      style={{ height: "calc(100vh - 300px)", overflow: "hidden" }}
    >
      {/* Tabs */}
      <div className="my-3 flex-shrink-0">
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      <div className="bg-white rounded-4 shadow-sm overflow-hidden d-flex flex-column flex-grow-1">
        {loading ? (
          <div className="d-flex align-items-center justify-content-center flex-grow-1">
            <Loading loadingColor={ACCENTCOLOR} />
          </div>
        ) : error ? (
          <div className="d-flex align-items-center justify-content-center flex-grow-1">
            <div className="text-center text-danger">Error: {error}</div>
          </div>
        ) : (
          <div
            className="d-flex flex-column flex-grow-1"
            style={{ minHeight: 0, overflow: "hidden" }}
          >
            {activeTab === "new-billing-payment" ? (
              <Table
                key="payment-table"
                columns={paymentColumns}
                data={paymentData}
                emptyMessage="No payment history found"
              />
            ) : activeTab === "new-billing-plan" ? (
              <Table
                key="plan-table"
                columns={planColumns}
                data={planData}
                emptyMessage="No plan history found"
              />
            ) : activeTab === "new-billing-plan-details" ? (
              <Table
                key="product-pricing-table"
                columns={productPricingColumns}
                data={productPricingData}
                emptyMessage="No product pricing data found"
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistorySection;
